import axios from "axios";

/**
 * The maintenance switch. Jake turns it on in AWS when the backend is down
 * (`/JobHarvestEnvs/<env>/MAINTENANCE_MODE`), the API's public `GET /status`
 * reports it, and every page shows MaintenanceOverlay until a check says it is
 * off again. The iOS app reads the same route on the same schedule.
 *
 * When we check:
 *  - once at start, so a page opened mid-outage says so straight away;
 *  - every 2 minutes while the tab is visible, and again the moment it is shown;
 *  - right after an API call fails with no response or a 5xx, at most once per
 *    15 seconds, so an outage reaches people who are mid-swipe without one
 *    status call per failed request.
 *
 * Never from a hidden tab, and a tab shown again within 30 seconds of the last
 * check does not check again.
 *
 * A failed or unreadable `/status` changes nothing: the switch is off until the
 * API says it is on, and stays on until the API says it is off. Showing an
 * outage screen because the status route itself hiccuped would be a false alarm,
 * and dropping the screen because the status route went down with everything
 * else would hand people a broken app.
 */

export const STATUS_INTERVAL_MS = 120_000;
export const FAILURE_CHECK_GAP_MS = 15_000;
export const MIN_CHECK_GAP_MS = 30_000;
const STATUS_TIMEOUT_MS = 8_000;
const MESSAGE_MAX = 500;

export const MAINTENANCE_COPY = {
  title: "We'll be right back",
  body: "JobHarvest is down right now and we're working on getting it back up. Please check back soon.",
  retry: "Try again",
};

/** `{ on, message }` from a `/status` body, or null when the body is not the contract's shape. */
export const parseStatus = (body) => {
  const maintenance = body?.maintenance;
  if (!maintenance || typeof maintenance.on !== "boolean") return null;
  const message = typeof maintenance.message === "string" ? maintenance.message.trim().slice(0, MESSAGE_MAX) : "";
  return { on: maintenance.on, message: message || null };
};

/**
 * One `GET /status`. Plain fetch rather than axios so the status call is never
 * itself counted as a failed API call by the interceptor below, and with no
 * credentials or custom headers so the browser sends it without a preflight.
 */
export const fetchStatus = async (baseUrl, fetchImpl = typeof fetch === "function" ? fetch : null) => {
  if (!baseUrl || !fetchImpl) return null;
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), STATUS_TIMEOUT_MS) : null;
  try {
    const response = await fetchImpl(`${baseUrl}/status`, {
      method: "GET",
      cache: "no-store",
      credentials: "omit",
      signal: controller?.signal,
    });
    if (!response.ok) return null;
    return parseStatus(await response.json());
  } catch {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

/** True for a failure that may mean the backend is down: no response at all, or a 5xx. */
export const isOutageLikeFailure = (error) => {
  if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") return false;
  const status = error?.response?.status;
  if (typeof status === "number") return status >= 500;
  return Boolean(error?.request) || error?.code === "ERR_NETWORK" || error?.message === "Network Error";
};

/**
 * The switch's state and the schedule that keeps it current. A factory, so the
 * tests can drive their own copy with fake timers; the app uses `maintenance`.
 */
export const createMaintenanceMonitor = ({
  baseUrl,
  fetchImpl,
  now = () => Date.now(),
  doc = typeof document !== "undefined" ? document : null,
} = {}) => {
  let state = { on: false, message: null };
  const listeners = new Set();
  let inFlight = null;
  let lastFailureCheckAt = -Infinity;
  let lastCheckAt = -Infinity;
  let interval = null;
  let started = false;

  const set = (patch) => {
    state = { ...state, ...patch };
    listeners.forEach((listener) => listener());
  };

  const check = () => {
    if (inFlight) return inFlight;
    lastCheckAt = now();
    inFlight = fetchStatus(baseUrl, fetchImpl).then((result) => {
      inFlight = null;
      if (result && (result.on !== state.on || result.message !== state.message)) set(result);
      return result;
    });
    return inFlight;
  };

  const isVisible = () => !doc || doc.visibilityState !== "hidden";

  const startInterval = () => {
    if (interval) return;
    interval = setInterval(() => {
      if (isVisible()) check();
    }, STATUS_INTERVAL_MS);
  };
  const stopInterval = () => {
    clearInterval(interval);
    interval = null;
  };

  const onVisibilityChange = () => {
    if (isVisible()) {
      if (now() - lastCheckAt >= MIN_CHECK_GAP_MS) check();
      startInterval();
    } else {
      stopInterval();
    }
  };

  return {
    getSnapshot: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    check,
    /** Called for every failed API call; checks at most once per FAILURE_CHECK_GAP_MS. */
    reportFailure: (error) => {
      if (!isOutageLikeFailure(error) || !isVisible()) return;
      if (now() - lastFailureCheckAt < FAILURE_CHECK_GAP_MS) return;
      lastFailureCheckAt = now();
      check();
    },
    start: () => {
      if (started) return;
      started = true;
      check();
      if (isVisible()) startInterval();
      doc?.addEventListener("visibilitychange", onVisibilityChange);
    },
    stop: () => {
      started = false;
      stopInterval();
      doc?.removeEventListener("visibilitychange", onVisibilityChange);
    },
  };
};

const apiBase = process.env.REACT_APP_API_DOMAIN;

export const maintenance = createMaintenanceMonitor({ baseUrl: apiBase });

/**
 * Every API call goes through the default axios instance (the slices call it
 * directly, BackendConnector wraps it), so one response interceptor sees them
 * all. Only calls to our API count; a third-party failure says nothing about
 * our backend.
 */
export const installApiFailureWatch = (monitor = maintenance, axiosInstance = axios, base = apiBase) => {
  // Installed once per axios instance: a second call (a test import, hot reload) would stack
  // another interceptor. Re-installing ejects the old one first.
  const previous = installed.get(axiosInstance);
  if (previous !== undefined) axiosInstance.interceptors.response.eject(previous);
  const id = axiosInstance.interceptors.response.use(undefined, (error) => {
    const url = error?.config?.url || "";
    if (base && url.startsWith(base)) monitor.reportFailure(error);
    return Promise.reject(error);
  });
  installed.set(axiosInstance, id);
  return id;
};
const installed = new WeakMap();
